import React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InlineFilters, TableSettingsMenu, type FilterField, type FilterPreset } from '../filters';
import DataTable, { Column } from '@/components/ui/data-table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

export interface GenericDataTableProps<T> {
    // Data and columns
    data: T[];
    columns: Column<T>[];
    visibleColumns: string[];

    // Filtering
    filters: Record<string, any>;
    filterFields: FilterField[];
    presets: FilterPreset[];

    // Loading and empty states
    isLoading?: boolean;
    emptyMessage?: string;
    loadingMessage?: string;

    // Selection
    selectedItems: string[];
    onSelectionChange: (selectedItems: string[]) => void;
    getItemId: (item: T) => string;

    // Sorting
    sortConfig: { key: keyof T; direction: 'asc' | 'desc' };
    onSort: (key: keyof T) => void;

    // Pagination
    totalItems: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;

    // Actions
    onExport?: () => void;
    isExporting?: boolean;
    onRowClick?: (item: T) => void;
    onFilterChange: (filters: Record<string, any>) => void;
    onClearFilters: () => void;
    onLoadPreset?: (preset: FilterPreset) => void;
    onSavePreset?: (preset: Omit<FilterPreset, 'id'>) => void;
    onColumnToggle: (columnId: string) => void;
    onResetColumns: () => void;

    // Customization
    className?: string;
    showExportButton?: boolean;
    showRowClick?: boolean;
    /**
     * Full-page list mode: the table fills the remaining height, only the rows
     * scroll, and the column header and pagination bar stay pinned. Requires the
     * page to render inside `<PageContainer fullHeight>`.
     */
    stickyLayout?: boolean;
    density?: 'comfortable' | 'compact';
    /** Replaces the default empty row. Use `<EmptyState>`. */
    emptyState?: React.ReactNode;
}

const GenericDataTable = <T extends Record<string, any>>({
    data,
    columns,
    visibleColumns,
    filters,
    filterFields,
    presets = [],
    isLoading = false,
    emptyMessage = 'No items found',
    loadingMessage = 'Loading...',
    selectedItems,
    onSelectionChange,
    getItemId,
    sortConfig,
    onSort,
    totalItems,
    totalPages,
    currentPage,
    pageSize,
    onPageChange,
    onPageSizeChange,
    onExport,
    isExporting = false,
    onRowClick,
    onFilterChange,
    onClearFilters,
    onLoadPreset,
    onSavePreset,
    onColumnToggle,
    onResetColumns,
    className = '',
    showExportButton = true,
    showRowClick = true,
    stickyLayout = false,
    density = 'compact',
    emptyState,
}: GenericDataTableProps<T>) => {
    return (
        <div className={cn('flex flex-col gap-4', stickyLayout && 'min-h-0 flex-1', className)}>
            {/* Preset Filters - removed in favor of views */}
            {presets.length > 0 && onLoadPreset && (
                <div className="flex shrink-0 flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
                    <span className="text-sm font-medium text-muted-foreground">
                        Quick Filters:
                    </span>
                    {presets.map(preset => (
                        <Button
                            key={preset.id}
                            variant="outline"
                            size="sm"
                            onClick={() => onLoadPreset(preset)}
                            className="text-xs"
                        >
                            {preset.name}
                        </Button>
                    ))}
                </div>
            )}

            {/* Inline Filters */}
            <InlineFilters
                className="shrink-0"
                fields={filterFields}
                filters={filters}
                onFilterChange={onFilterChange}
                onClearFilters={onClearFilters}
            />

            {/* Table Settings Row - positioned in top right of table */}
            <div className="flex shrink-0 items-center justify-end">
                <TableSettingsMenu
                    filters={filters}
                    presets={presets}
                    onLoadPreset={onLoadPreset}
                    onSavePreset={onSavePreset}
                    columns={columns}
                    visibleColumns={visibleColumns}
                    onColumnToggle={onColumnToggle}
                    onResetColumns={onResetColumns}
                    onExport={showExportButton ? onExport : undefined}
                    isExporting={isExporting}
                />
            </div>

            {/* Table */}
            <DataTable
                data={data}
                columns={
                    visibleColumns.length > 0
                        ? columns.filter(col => visibleColumns.includes(col.id))
                        : columns
                }
                isLoading={isLoading}
                skeletonRows={pageSize}
                onExport={onExport}
                sortConfig={sortConfig}
                onSort={onSort}
                filters={{}}
                onFilterChange={onFilterChange}
                selectedItems={selectedItems}
                onSelectionChange={onSelectionChange}
                getItemId={getItemId}
                onRowClick={showRowClick ? onRowClick : undefined}
                emptyMessage={emptyMessage}
                loadingMessage={loadingMessage}
                showAdvancedFilters={false}
                showColumnSettings={false}
                stickyLayout={stickyLayout}
                density={density}
                emptyState={emptyState}
            />

            {/* Pagination. In sticky mode this bar stays visible at the bottom of the
                viewport, so paging never requires scrolling to the end of the rows. */}
            {(totalPages > 1 || onPageSizeChange) && (
                <Card className={cn('shrink-0', stickyLayout && 'sticky bottom-0 z-20')}>
                    <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="text-sm text-muted-foreground">
                                    Showing {(currentPage - 1) * pageSize + 1} to{' '}
                                    {Math.min(currentPage * pageSize, totalItems)} of {totalItems}{' '}
                                    items
                                </div>
                                {onPageSizeChange && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-muted-foreground">Show:</span>
                                        <Select
                                            value={pageSize.toString()}
                                            onValueChange={value =>
                                                onPageSizeChange(parseInt(value))
                                            }
                                        >
                                            <SelectTrigger className="w-20">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="10">10</SelectItem>
                                                <SelectItem value="25">25</SelectItem>
                                                <SelectItem value="50">50</SelectItem>
                                                <SelectItem value="100">100</SelectItem>
                                                <SelectItem value="250">250</SelectItem>
                                                <SelectItem value="500">500</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>
                            {totalPages > 1 &&
                                (() => {
                                    // Calculate page range to show (show current page and 2 pages on each side)
                                    const getPageNumbers = () => {
                                        const pages: (number | string)[] = [];
                                        const maxVisible = 5;
                                        let startPage = Math.max(
                                            1,
                                            currentPage - Math.floor(maxVisible / 2)
                                        );
                                        let endPage = Math.min(
                                            totalPages,
                                            startPage + maxVisible - 1
                                        );

                                        // Adjust start if we're near the end
                                        if (endPage - startPage < maxVisible - 1) {
                                            startPage = Math.max(1, endPage - maxVisible + 1);
                                        }

                                        // Add first page and ellipsis if needed
                                        if (startPage > 1) {
                                            pages.push(1);
                                            if (startPage > 2) {
                                                pages.push('...');
                                            }
                                        }

                                        // Add page numbers in range
                                        for (let i = startPage; i <= endPage; i++) {
                                            pages.push(i);
                                        }

                                        // Add ellipsis and last page if needed
                                        if (endPage < totalPages) {
                                            if (endPage < totalPages - 1) {
                                                pages.push('...');
                                            }
                                            pages.push(totalPages);
                                        }

                                        return pages;
                                    };

                                    const pageNumbers = getPageNumbers();

                                    return (
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    onPageChange(Math.max(1, currentPage - 1))
                                                }
                                                disabled={currentPage <= 1}
                                            >
                                                Previous
                                            </Button>
                                            <div className="flex items-center gap-1">
                                                {pageNumbers.map((pageNum, index) => {
                                                    if (pageNum === '...') {
                                                        return (
                                                            <span
                                                                key={`ellipsis-${index}`}
                                                                className="px-2 text-muted-foreground"
                                                            >
                                                                ...
                                                            </span>
                                                        );
                                                    }
                                                    const num = pageNum as number;
                                                    return (
                                                        <Button
                                                            key={num}
                                                            variant={
                                                                currentPage === num
                                                                    ? 'default'
                                                                    : 'outline'
                                                            }
                                                            size="sm"
                                                            onClick={() => onPageChange(num)}
                                                            disabled={currentPage === num}
                                                        >
                                                            {num}
                                                        </Button>
                                                    );
                                                })}
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    onPageChange(
                                                        Math.min(totalPages, currentPage + 1)
                                                    )
                                                }
                                                disabled={currentPage >= totalPages}
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    );
                                })()}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default GenericDataTable;
